/**
 * Detector de fluxo de salvamento (TEMP vs NORMAL)
 * 
 * Consolidação da lógica de detecção espalhada no código
 * Origem: linhas 1215-1234 do useSaveController.ts
 */

import { PATH_PATTERN_TEMP } from '../constants/saveConstants';

/**
 * Tipo de fluxo de salvamento
 */
export type SaveFlow = 'temp' | 'normal';

/**
 * Resultado da detecção de fluxo
 */
export interface FlowDetectionResult {
  /**
   * Tipo de fluxo detectado
   */
  flow: SaveFlow;
  
  /**
   * Razão da decisão (para debugging)
   */
  reason: string;
  
  /**
   * Detalhes da detecção
   */
  details: {
    isTempFlag: boolean;
    looksUNC: boolean;
    hasProId: boolean;
    proId: number;
  };
}

/**
 * Detecta se o salvamento deve seguir fluxo TEMP ou NORMAL
 * 
 * Lógica de detecção:
 * 1. Se fluxo.tipo === 'temp' -> TEMP
 * 2. Se caminho contém padrão TEMP E pro_id ausente -> TEMP
 * 3. Caso contrário -> NORMAL
 * 
 * @param saveData - Dados do salvamento
 * @param savedFilePath - Caminho do arquivo salvo (opcional)
 * @returns Resultado da detecção
 * 
 * @example
 * const result = detectSaveFlow(saveData, savedFilePath);
 * if (result.flow === 'temp') {
 *   // Usar API de orçamentos temporários
 * } else {
 *   // Usar API de produção
 * }
 */
export function detectSaveFlow(
  saveData: any,
  savedFilePath?: string
): FlowDetectionResult {
  // 1. Verificar flag explícita de fluxo
  const rawFluxo = saveData?.fluxo?.tipo 
    || (typeof saveData?.fluxo === 'string' ? saveData.fluxo : undefined);
  const isTempFlag = rawFluxo === 'temp';
  
  if (isTempFlag) {
    return {
      flow: 'temp',
      reason: 'fluxo.tipo === "temp"',
      details: {
        isTempFlag: true,
        looksUNC: false,
        hasProId: false,
        proId: 0,
      },
    };
  }
  
  // 2. Verificar padrão de caminho (UNC pattern)
  const intern = String(saveData?.diretorios?.interno || '');
  const full = String(savedFilePath || saveData?.caminho_arquivo || '');
  const looksUNC = intern.includes(PATH_PATTERN_TEMP) || full.includes(PATH_PATTERN_TEMP);
  
  // 3. Verificar pro_id
  const proIdNum = Number(
    saveData?.produto?.pro_id 
    || saveData?.produto?.id 
    || saveData?.regras?.pro_id 
    || 0
  );
  const hasProId = proIdNum > 0;
  
  // 4. Decisão final: TEMP se looks UNC E sem pro_id
  const isTemp = looksUNC && !hasProId;
  
  return {
    flow: isTemp ? 'temp' : 'normal',
    reason: isTemp 
      ? `Caminho contém "${PATH_PATTERN_TEMP}" e pro_id ausente`
      : 'pro_id presente ou caminho não é temporário',
    details: {
      isTempFlag,
      looksUNC,
      hasProId,
      proId: proIdNum,
    },
  };
}

/**
 * Versão simplificada que retorna apenas o tipo de fluxo
 * 
 * @param saveData - Dados do salvamento
 * @param savedFilePath - Caminho do arquivo salvo (opcional)
 * @returns 'temp' ou 'normal'
 */
export function detectSaveFlowSimple(
  saveData: any,
  savedFilePath?: string
): SaveFlow {
  return detectSaveFlow(saveData, savedFilePath).flow;
}

/**
 * Verifica se é fluxo TEMP (helper)
 * 
 * @param saveData - Dados do salvamento
 * @param savedFilePath - Caminho do arquivo salvo (opcional)
 * @returns true se fluxo TEMP, false caso contrário
 */
export function isTempFlow(
  saveData: any,
  savedFilePath?: string
): boolean {
  return detectSaveFlowSimple(saveData, savedFilePath) === 'temp';
}

/**
 * Verifica se é fluxo NORMAL/Produção (helper)
 * 
 * @param saveData - Dados do salvamento
 * @param savedFilePath - Caminho do arquivo salvo (opcional)
 * @returns true se fluxo NORMAL, false caso contrário
 */
export function isNormalFlow(
  saveData: any,
  savedFilePath?: string
): boolean {
  return detectSaveFlowSimple(saveData, savedFilePath) === 'normal';
}

